import styled from "styled-components";
import { FLEX } from "styles/flex";
import { COLORS } from "styles/colors";
import WARNING_ICON from "assets/icons/warning_circle_red_border.svg";

import { calcRem } from "styles/theme";
import BigButton from "components/atoms/buttons/BigButton";
import { Text } from "components/atoms/text/Text";
import StorageBar from "components/blocks/storage/StorageBar";
import * as mediaQuery from "components/MediaQuery";
import SHOP_ICON from "assets/icons/shop.svg";
import { ReportHealthDetailStorage } from "api/interfaces/reportInterface.interface";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import { useQuery } from "react-query";
import { getReportHealthDetailStorage } from "api/reportAPI";
import { SubTitle } from "pages/dashboard/map/sub/leftAside/detail/RecorderDetailStyled";
import Spinner from "components/atoms/loader/Spinner";
import { IoCloud } from "react-icons/io5";
import { FaBoxArchive } from "react-icons/fa6";
import { useCallback, useEffect } from "react";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import useStorageUsage from "hook/useStorageUsage";

type Props = {
  recorderId: string;
};

export default function Storage({ recorderId }: Props): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const isMap = location.pathname.includes("map");

  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { percentage: cloudPercent, isLoading: isCloudStorageLoading } =
    useStorageUsage();

  const getHealthStorage = useQuery(
    ["reportHealthStorage", recorderId],
    () =>
      getReportHealthDetailStorage({
        recorderId: recorderId,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      staleTime: 60000, //1분
      refetchOnWindowFocus: false,
    }
  );
  const isFetching = isCloudStorageLoading || getHealthStorage.isLoading;
  const refecth = useCallback(() => {
    getHealthStorage.refetch();
  }, []);
  useStompDebouncedRefetch({
    stompKey: ["reportDetailUpdated"] as StompDateSignalKey[],
    refetch: refecth,
  });

  const data: ReportHealthDetailStorage[] = getHealthStorage.data?.result ?? [];

  useEffect(() => {
    if (recorderId) {
      getHealthStorage.refetch();
    }
  }, [recorderId]);

  return (
    <>
      {isFetching ? (
        isMap ? (
          <></>
        ) : (
          <Spinner />
        )
      ) : (
        <Inner isMap={isMap}>
          {isMap && <SubTitle>Storage</SubTitle>}

          {!isMap && cloudPercent > 90 && (
            <Warning>
              <img src={WARNING_ICON} alt="warning circle icon" />
              Cloud storage capacity is nearly full, consider purchasing more
              storage or deleting old footage.
            </Warning>
          )}

          <StorageWrapper isMap={isMap}>
            <StorageItem isMap={isMap}>
              <span className="title">
                <IoCloud />
                <Text bold fontSize={isMap ? 12 : 16}>
                  Cloud Storage
                </Text>
              </span>
              <div className="title">
                <StorageBar />
              </div>
              {!isMap && cloudPercent > 90 && (
                <ButtonWrapper>
                  {selectedAccount.accountLevel === LEVEL_TYPE.EU && (
                    <BigButton
                      // isDisabled
                      onClickBtn={() =>
                        navigate("/settings/license", {
                          state: selectedAccount,
                        })
                      }
                      label="Buy More Storage"
                      icon={SHOP_ICON}
                      data-testid="reports-view-type-button-navigate"
                    />
                  )}
                </ButtonWrapper>
              )}
            </StorageItem>

            <StorageItem isMap={isMap}>
              <span className="title">
                <FaBoxArchive />
                <Text bold fontSize={isMap ? 12 : 16}>
                  {"Local Storage"}
                </Text>
              </span>
              {data.map((item: ReportHealthDetailStorage, index: number) => (
                <LocalItem key={index}>
                  <HddLabel>{item.name.replaceAll("//", "\\")}</HddLabel>
                  <StorageBar
                    usedStorage={item.total - item.free}
                    totalStorage={item.total}
                    isMB
                  />
                </LocalItem>
              ))}
            </StorageItem>
          </StorageWrapper>
        </Inner>
      )}
    </>
  );
}

const Inner = styled.div<{ isMap: boolean }>`
  ${FLEX.FlexStartCenter};
  flex-direction: column;
  width: 100%;

  ${({ isMap }) =>
    isMap
      ? `
      border-bottom: 1px solid ${COLORS.BORDER};
    `
      : `
  ${mediaQuery.isDefault} {
    padding-top: 30px;
  }
  `}
`;

const Warning = styled.span`
  color: ${COLORS.PROGRESSBAR};
  ${FLEX.FlexCenterCenter};
  gap: 10px;
  padding-bottom: 20px;

  img {
    width: 20px;
  }
`;
const LocalItem = styled.div`
  margin-bottom: 16px;
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 6px;
`;
const HddLabel = styled.span`
  padding: 4px 6px;
  font-size: ${calcRem(14)};
  font-weight: 500;
  background-color: ${({ theme }) =>
    theme.value === "light" ? COLORS.BORDER : COLORS.PRIMARY1};
  color: ${COLORS.WHITE};
  border-radius: 2px;
  white-space: nowrap;
`;

const ButtonWrapper = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 15px;
  width: 100%;
`;

const StorageWrapper = styled.ul<{ isMap: boolean }>`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  padding-top: 10px;
  width: 100%;

  ${({ isMap }) =>
    isMap
      ? `
      gap: 10px;
    `
      : `
    ${mediaQuery.isDefault} {
    width: 60%;
    gap: 50px;
  }
  `}

  ${mediaQuery.isMobile} {
    gap: 20px;
  }
`;

const StorageItem = styled.li<{ isMap: boolean }>`
  width: 100%;

  ${({ isMap }) =>
    isMap &&
    `
    $${FLEX.FlexStartStart};
    flex-direction: column;

    > div {
        width: 100%;
      }
  `}

  .title {
    ${FLEX.FlexStartCenter};
    gap: 8px;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.COLORS.WHITE};

    > img {
      width: 20px;
    }

    ${({ isMap }) =>
      isMap &&
      `
      width 100%;
      aligtn-items:flex-start;
      gap: 6px;

      > img {
        width: 14px;
      }
    `}
  }
`;
